import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Flag, Trash2, Users, MessageSquare, Check, History, UserX } from 'lucide-react';
import AdminReportsAuditPanel from '../components/AdminReportsAuditPanel';

const AdminGroupReports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [auditReportId, setAuditReportId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter ? `?kind=${filter}` : '';
      const res = await axios.get(`/api/admin/group-reports${q}`);
      setReports(res.data || []);
    } catch (e) {
      toast.error('Failed to load reports');
    } finally { setLoading(false); }
  };

  const [resolveDialog, setResolveDialog] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => { load(); }, [filter]);

  const resolve = (id, action) => {
    setResolveDialog({ id, action });
    setResolveNote('');
  };

  const confirmResolveWithNote = async () => {
    if (!resolveDialog) return;
    const clean = resolveNote.trim();
    if (clean.length < 3) { toast.error('Note d\'au moins 3 caractères obligatoire'); return; }
    setResolving(true);
    try {
      await axios.post(`/api/admin/reports-audit/group/${resolveDialog.id}/note`, { note: clean, action: resolveDialog.action });
      await axios.post(`/api/admin/group-reports/${resolveDialog.id}/resolve`, { action: resolveDialog.action });
      toast.success('Signalement traité et note enregistrée');
      setResolveDialog(null); setResolveNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setResolving(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 pb-20" data-testid="admin-group-reports-page">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Flag className="w-5 h-5 text-amber-400" /> Signalements Groupes ({reports.length})
        </h1>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { id: '', label: 'Tous' },
          { id: 'group', label: 'Groupes' },
          { id: 'group_message', label: 'Messages' },
          { id: 'group_member', label: 'Utilisateurs (GPT Guyane / Africa)' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            className={`px-3 py-1.5 rounded-full text-sm ${
              filter === t.id ? 'bg-amber-500 text-black' : 'bg-white/10'
            }`}
            data-testid={`filter-${t.id || 'all'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <AdminReportsAuditPanel
        kind="group"
        selectedReportId={auditReportId}
        onCloseSelected={() => setAuditReportId(null)}
      />

      {loading ? (
        <p className="text-center text-gray-400">Chargement…</p>
      ) : reports.length === 0 ? (
        <p className="text-center text-gray-500 py-12 italic">Aucun signalement en attente</p>
      ) : (
        <div className="space-y-3">
          {reports.map(r => (
            <div key={r.id} className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3" data-testid={`report-${r.id}`}>
              <div className="flex items-center gap-2">
                {r.kind === 'group' ? <Users className="w-4 h-4 text-violet-400" /> : r.kind === 'group_member' ? <UserX className="w-4 h-4 text-red-400" /> : <MessageSquare className="w-4 h-4 text-violet-400" />}
                <span className="text-xs uppercase tracking-wide text-gray-400">
                  {r.kind === 'group' ? 'Groupe signalé' : r.kind === 'group_member' ? 'Utilisateur signalé' : 'Message signalé'}
                </span>
                {r.auto_flagged && <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-300" data-testid={`auto-flag-${r.id}`}>IA</span>}
                {r.is_system_group && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">{r.group?.system_kind === 'regional_lang_africa' ? 'GPT Africa' : 'GPT Guyane'}</span>}
                <span className="ml-auto text-[10px] text-gray-500">{r.created_at?.slice(0, 19)}</span>
              </div>

              <div className="flex items-center gap-3">
                {r.group?.profile_image_url ? (
                  <img src={r.group.profile_image_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-violet-700/30 flex items-center justify-center">
                    <Users className="w-5 h-5 text-violet-300" />
                  </div>
                )}
                <div>
                  <p className="font-semibold">{r.group?.name || '(deleted)'}</p>
                  <p className="text-xs text-gray-400">{r.group?.theme || ''}</p>
                </div>
              </div>

              {r.kind === 'group_message' && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-1">Message ({r.message_type}):</p>
                  {r.message_type === 'text' ? (
                    <p className="text-sm whitespace-pre-wrap break-words">{r.message_content || '(empty)'}</p>
                  ) : r.message_media_url ? (
                    <div>
                      {r.message_type === 'image' && <img src={r.message_media_url} alt="" className="max-w-xs rounded" />}
                      {r.message_type === 'video' && <video src={r.message_media_url} controls className="max-w-xs rounded" />}
                      {r.message_type === 'voice' && <audio src={r.message_media_url} controls />}
                    </div>
                  ) : <p className="text-sm text-gray-500 italic">(no preview)</p>}
                </div>
              )}

              {r.reported_user && (
                <div className="text-xs text-gray-400" data-testid={`reported-user-${r.id}`}>
                  <span className="text-gray-300">Utilisateur visé :</span> {r.reported_user.name || '?'} (@{r.reported_user.username || '?'})
                </div>
              )}
              <div className="text-xs text-gray-400">
                <span className="text-gray-300">Reporter:</span> {r.reporter_id === 'system_dsia_moderator' ? 'Destine IA (auto)' : `${r.reporter?.name || '?'} (@${r.reporter?.username || '?'})`}
              </div>
              {r.reason && (
                <div className="text-xs bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
                  <span className="text-amber-300 font-semibold">Raison: </span>{r.reason}
                </div>
              )}

              <div className="flex gap-2 pt-1 flex-wrap">
                <button
                  onClick={() => setAuditReportId(r.id)}
                  className="px-3 py-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-sm font-semibold flex items-center justify-center gap-1"
                  data-testid={`open-history-${r.id}`}
                  title="Voir l'historique et ajouter une note"
                >
                  <History className="w-4 h-4" /> Historique
                </button>
                <button
                  onClick={() => resolve(r.id, 'dismiss')}
                  className="flex-1 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold flex items-center justify-center gap-1"
                  data-testid={`dismiss-${r.id}`}
                >
                  <Check className="w-4 h-4" /> Rejeter
                </button>
                {r.kind === 'group_message' && (
                  <button
                    onClick={() => resolve(r.id, 'delete_message')}
                    className="flex-1 py-2 rounded-lg bg-orange-500/20 hover:bg-orange-500/40 text-orange-300 text-sm font-semibold flex items-center justify-center gap-1"
                    data-testid={`delete-msg-${r.id}`}
                  >
                    <Trash2 className="w-4 h-4" /> Supprimer message
                  </button>
                )}
                {r.is_system_group && (r.reported_user_id || r.message_user_id) && (
                  <button
                    onClick={() => resolve(r.id, 'exclude_member')}
                    className="flex-1 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-300 text-sm font-semibold flex items-center justify-center gap-1"
                    data-testid={`exclude-member-${r.id}`}
                  >
                    <UserX className="w-4 h-4" /> Exclure du groupe
                  </button>
                )}
                {!r.is_system_group && (
                <button
                  onClick={() => resolve(r.id, 'delete_group')}
                  className="flex-1 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-300 text-sm font-semibold flex items-center justify-center gap-1"
                  data-testid={`delete-group-${r.id}`}
                >
                  <Trash2 className="w-4 h-4" /> Supprimer groupe
                </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Mandatory-note dialog on resolution */}
      {resolveDialog && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="resolve-note-modal">
          <div className="bg-gray-900 border border-white/10 rounded-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold">Action : {resolveDialog.action}</h3>
            <p className="text-sm text-gray-400">Merci de laisser une note pour indiquer <strong className="text-white">quelles actions ont été prises</strong>. Elle sera enregistrée avec votre nom et la date.</p>
            <textarea value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} rows={4} autoFocus placeholder="Ex: Groupe supprimé, membres notifiés, aucune action nécessaire…" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-magenta-500 text-sm" data-testid="resolve-note-textarea" />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setResolveDialog(null); setResolveNote(''); }} className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm">Annuler</button>
              <button onClick={confirmResolveWithNote} disabled={resolving || resolveNote.trim().length < 3} className="px-4 py-2 rounded-full bg-gradient-to-r from-magenta-500 to-violet-600 text-white text-sm font-semibold disabled:opacity-50" data-testid="resolve-note-confirm">{resolving ? 'Envoi…' : 'Confirmer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGroupReports;
